import { isUsageAndBillingPage } from '@common/credits/ProfessionalEdition/utils';
import { EntityIcon } from '@common/Entity/EntityIcons';
import { FabricSetupDialog } from '@common/Fabric/FabricSetup';
import { useUserFabrics } from '@common/Fabric/hooks';
import { useOnBoardingSkipFlow } from '@common/onboarding/hooks';
import { UserTeamsInfoAspectQuery } from '@common/queries/user';
import { useReplayModeType, useSetReplayUserPreference } from '@common/Replay/utils';
import { Entity, getAppEntityName } from '@common/types/Entity';
import { Private_Routes, Public_Routes } from '@common/url';
import { HUB_URL } from '@pages/PackageHub/constants';
import { GuestUserClickResultEnum, useGuestUserClick } from '@pages/Sql/Replay/Share/utils';
import { Callable } from '@prophecy/interfaces/generic';
import { Button, Dialog, Popover, resolveCssVariable, SIDE_NAVBAR_WIDTH, Stack, theme, Tooltip } from '@prophecy/ui';
import { toast } from '@prophecy/ui-v3';
import {
  ActivityIcon,
  BookOpenAIcon,
  BuildingBIcon,
  DotsHorizontalIcon,
  DownloadBIcon,
  DownloadDIcon,
  GemIcon,
  HelpCircleIcon,
  LifeBuoyBIcon,
  LogoProphecyIcon,
  LogOutDIcon,
  MessageChatSquareIcon,
  MetadataIcon,
  PlusIcon,
  SettingsBIcon
} from '@prophecy/ui-v3/Icons';
import { DialogResponseAction } from '@prophecy/ui/Dialog/types';
import { ICON_CLASS } from '@prophecy/ui/Icons/tokens';
import { hideScrollbarCss } from '@prophecy/ui/Misc/HideScrollbar';
import { navigateAndRefresh } from '@prophecy/utils/history';
import { parseJSON } from '@prophecy/utils/string';
import { isReplayMode } from 'frontend/core/src/utils/replayToken';
import { isShareMode } from 'frontend/core/src/utils/shareToken';
import React, { useEffect, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle, css } from 'styled-components';

import { useAppMetadata } from '../context/appMetadata';
import { useEditionConfig, useHasTranspilerAccess, useIsSparkSupported } from '../context/edition';
import { useEnabledTranspilersAndLanguages } from '../context/enabledTranspilers';
import { useEnvConfig } from '../context/envConfig';
import { useGlobalVariables } from '../context/globalConfig/securedConfigHook';
import { completeLogoutAndReloadPage } from '../data/apis/api';
import { redirectToAuth } from '../data/rest/redirectToAuth';
import { useGraphQlQuery } from '../data/util';
import { AspectKind } from '../graphqlTypes/enums';
import { AiChatButton } from '../InKeepAI';
import { downloadWebSocketLogsViaWorker } from '../logger/websocketMessageLogger';
import { allowedDomain } from '../pages/auth/common';
import {
  isDebugMode,
  isWebSocketLoggingEnabled,
  LAST_JOB_IDE_URL,
  LAST_OBSERVATION_IDE_URL,
  LAST_VISITED_IDE_URL
} from '../utils/localstorage-keys';

type NavItemStyleProps = { $active?: boolean; disabled?: boolean };

const StyledLogo = styled(LogoProphecyIcon)`
  width: ${theme.sizes.x32};
  height: ${theme.sizes.x32};
  cursor: pointer;
  border-radius: ${theme.radius.x8};
`;

const StyledLogoLink = styled(Link)`
  &&:focus-visible {
    outline: none;
    ${StyledLogo} {
      box-shadow: ${theme.focusRing.shadow.xs};
    }
  }
`;

const StyledSideNavbar = styled(Stack)`
  width: ${SIDE_NAVBAR_WIDTH};
  pointer-events: all;
  background-color: ${theme.colors.gray900};
  padding: ${theme.spaces.x12} 0;
  font-family: ${theme.fontFamily.text};
  flex-shrink: 0;
  overflow: auto;
  ${hideScrollbarCss};
`;

const navItemCSS = css<NavItemStyleProps>`
  color: ${theme.colors.white};
  border-radius: ${theme.radius.x8};
  height: ${theme.sizes.x40};
  width: ${theme.sizes.x40};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.gray300};

  &:hover:not([disabled]) {
    color: ${theme.colors.white};
    background-color: ${theme.colors.grayBlue700};
  }

  &[disabled] {
    color: ${theme.colors.gray500};
    pointer-events: none;
  }

  ${(props) =>
    props.$active &&
    `
    &&& {
      color: ${theme.colors.white};
      background-color: ${theme.colors.brand600};
    }
  `}

  .${ICON_CLASS} {
    color: inherit;
    font-size: ${theme.sizes.x24};
  }

  &&:focus-visible {
    outline: none;
    box-shadow: ${theme.focusRing.shadow.xs};
  }
`;

const StyledNavLink = styled(Button)<NavItemStyleProps>`
  ${navItemCSS}
`;

const StyledNavAnchor = styled.a<NavItemStyleProps>`
  ${navItemCSS}
`;

const StyledNavButton = styled.button`
  all: unset;
  ${navItemCSS}

  &[data-state="open"] {
    background-color: ${theme.colors.brand500};
    color: ${theme.colors.white};
  }
`;

const navItemCSSDark = css<NavItemStyleProps>`
  all: unset;

  ${navItemCSS}

  color: ${theme.colors.gray900};
  &&:hover {
    color: ${theme.colors.white};
    background: ${theme.colors.brand500};
  }

  &&:focus-visible {
    outline: none;
    box-shadow: ${theme.focusRing.shadow.xs};
  }
`;

const StyledNavLinkDark = styled(Button)<NavItemStyleProps>`
  ${navItemCSSDark}
  &[disabled] {
    color: ${theme.colors.gray700};
    pointer-events: none;
  }
` as typeof Button;

const StyledNavAnchorDark = styled.a<NavItemStyleProps>`
  ${navItemCSSDark}
`;

const Divider = styled.hr`
  background-color: ${theme.colors.gray600};
  height: 1px;
  border: none;
  border-radius: ${theme.radius.x16};
  width: ${theme.sizes.x40};
`;

const MoreNavPopoverStyles = createGlobalStyle`
  .side-nav-more-popover {
    width: ${SIDE_NAVBAR_WIDTH};
    min-width: ${SIDE_NAVBAR_WIDTH};
    padding: ${theme.spaces.x6} 0;
    
  }
`;

type NavItemProps = {
  to: string;
  redirectURL: string;
  message: string;
  external?: boolean;
  activeOnRoutes?: string[];
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onDisabledNavClick?: (e: React.MouseEvent) => void;
  dataTestId: string;
};

const showAiAssistant = allowedDomain.includes(window.location.host);

function NavTooltip({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Tooltip title={title} placement='right'>
      {children}
    </Tooltip>
  );
}

const hasOnboardingUrl = (pathName: string) => {
  return (
    pathName.endsWith('onboarding') || pathName.endsWith('onboarding/replay') || pathName.endsWith('onboarding/replay/')
  );
};

const handleOnboardingSkip = async (isOnboardingUrl: boolean, message: string, okButtonText: string) => {
  return await Dialog.warning({
    'data-test-id': 'disabled-nav-warning-dialog',
    title: '',
    subTitle: isOnboardingUrl ? 'Do you want to skip onboarding?' : message,
    children: null,
    closeButton: isOnboardingUrl ? { children: 'Cancel' } : null,
    okButton: { children: okButtonText, block: false }
  });
};

const computeOkButtonText = (
  isProfessionalEditionEnabled: boolean,
  isOnboardingPage: boolean,
  pathName: string,
  isNavItemDisabled: boolean = false
) => {
  if (isProfessionalEditionEnabled) {
    if (isUsageAndBillingPage(pathName)) {
      return 'OK';
    }
    if (isOnboardingPage && !isNavItemDisabled) {
      return 'Skip';
    }
    return 'Upgrade Now';
  }

  if (isOnboardingPage) {
    return 'Skip';
  }

  return 'OK';
};

function NavItem({
  to,
  onClick,
  title,
  message,
  external,
  redirectURL,
  disabled,
  activeOnRoutes = [`${to}/*`],
  children,
  onDisabledNavClick,
  dataTestId
}: NavItemProps) {
  const location = useLocation();
  const globalConfigManager = useGlobalVariables();
  const { onSkip, navigate, sparkOnboardingUrl, sqlOnboardingUrl } = useOnBoardingSkipFlow();
  const pathName = location.pathname;
  const urlWithSearch = pathName + location.search;
  const isOnboardingIDE = urlWithSearch === sparkOnboardingUrl || urlWithSearch === sqlOnboardingUrl;
  const isOnboardingPage = hasOnboardingUrl(pathName) || isOnboardingIDE;
  const isSharedProjectOrReplayMode = isShareMode() || isReplayMode();
  const isProfessionalEditionEnabled = globalConfigManager.isProfessionalEditionEnabled;
  const guestUserClick = useGuestUserClick();

  const handleDisabledNavClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const okButtonText = computeOkButtonText(isProfessionalEditionEnabled, isOnboardingPage, pathName, true);

    const allowedDialogResult = await handleOnboardingSkip(isOnboardingIDE, message, okButtonText);
    if (allowedDialogResult === Dialog.ResponseAction.okButton) {
      if (isProfessionalEditionEnabled) {
        navigate(Private_Routes.Settings.tab.getUrl({ tab: 'usage&Billing' }));
        return;
      }
      if (isOnboardingPage) {
        await onSkip();
        const redirectURL = globalConfigManager.getFabricStatus
          ? ''
          : Private_Routes.Create_Entity.tab.getUrl({ tab: Entity.Fabric.toLowerCase() });
        navigate(redirectURL);
      } else if (redirectURL && redirectURL !== pathName) {
        navigate(redirectURL);
      }
    }
  };

  const { setReplayCompletedUserPreference, skipOnboarding } = useSetReplayUserPreference();

  const handleNavItemClickForSharedOrReplayMode = async (e: React.MouseEvent) => {
    e.preventDefault(); // Always prevent the default Link navigation first
    await setReplayCompletedUserPreference();
    const response = await guestUserClick();

    switch (response) {
      case GuestUserClickResultEnum.login:
        redirectToAuth();
        break;
      case GuestUserClickResultEnum.leave:
        window.onbeforeunload = null;
        navigateAndRefresh(to);
        break;
      case GuestUserClickResultEnum.cancel:
        // User cancelled, do nothing
        break;
    }
  };

  const routeMatched = activeOnRoutes.some((pattern) => {
    const match = matchPath(pattern, location.pathname);
    if (match) {
      return true;
    }
    return false;
  });

  const handleNavItemClick = async (e: React.MouseEvent) => {
    e.preventDefault(); // Always prevent the default Link navigation first
    if (isProfessionalEditionEnabled && isOnboardingPage) {
      const okButtonText = computeOkButtonText(isProfessionalEditionEnabled, isOnboardingPage, pathName);

      const allowedDialogResult = await handleOnboardingSkip(isOnboardingPage, message, okButtonText);
      if (allowedDialogResult === DialogResponseAction.okButton) {
        skipOnboarding();
      } else {
        return false;
      }
    }
    navigate(to);
    onClick?.(e);
  };

  let nav;
  if (external) {
    nav = (
      <StyledNavAnchor onClick={handleNavItemClick} href={to} aria-label={`Go to ${title}`} data-test-id={dataTestId}>
        {children}
      </StyledNavAnchor>
    );
  } else if (disabled) {
    nav = (
      <StyledNavLink
        variant='plain'
        onClick={onDisabledNavClick || handleDisabledNavClick}
        $active={routeMatched}
        aria-label={`Go to ${title}`}
        data-test-id={dataTestId}>
        {children}
      </StyledNavLink>
    );
  } else if (isSharedProjectOrReplayMode) {
    nav = (
      <StyledNavLink
        as={Link}
        to={to}
        onClick={handleNavItemClickForSharedOrReplayMode}
        $active={routeMatched}
        aria-label={`Go to ${title}`}
        data-test-id={dataTestId}>
        {children}
      </StyledNavLink>
    );
  } else {
    nav = (
      <StyledNavLink
        as={Link}
        to={to}
        onClick={handleNavItemClick}
        $active={routeMatched}
        aria-label={`Go to ${title}`}
        data-test-id={dataTestId}>
        {children}
      </StyledNavLink>
    );
  }

  return <NavTooltip title={title}>{nav}</NavTooltip>;
}

function MoreNavPopover({ onClose }: { disabled: boolean; onClose: () => void }) {
  const { hasReplayReadOnlyMode } = useReplayModeType();
  const navigate = useNavigate();
  const globalConfigManager = useGlobalVariables();
  const pathname = useLocation().pathname;
  const isOnboardingPage = hasOnboardingUrl(pathname);
  const guestUserClick = useGuestUserClick();
  const { setReplayCompletedUserPreference, skipOnboarding } = useSetReplayUserPreference();
  const handleSettingsClick = async (e: React.MouseEvent) => {
    e.preventDefault(); // Always prevent the default Link navigation first
    if (isOnboardingPage && globalConfigManager.isProfessionalEditionEnabled) {
      const allowedDialogResult = await handleOnboardingSkip(
        isOnboardingPage,
        '',
        computeOkButtonText(globalConfigManager.isProfessionalEditionEnabled, isOnboardingPage, pathname)
      );
      if (allowedDialogResult === DialogResponseAction.okButton) {
        skipOnboarding();
      } else {
        return false;
      }
    }
    if (hasReplayReadOnlyMode) {
      await setReplayCompletedUserPreference();
      const response = await guestUserClick();
      if (response === GuestUserClickResultEnum.login) {
        redirectToAuth();
      } else if (response === GuestUserClickResultEnum.leave) {
        window.onbeforeunload = null;
        navigateAndRefresh(Private_Routes.Settings.home.getUrl());
      }
      return;
    }

    navigate(Private_Routes.Settings.home.getUrl());
  };

  return (
    <Stack gap={theme.spaces.x8} alignY='center'>
      <MoreNavPopoverStyles />

      <NavTooltip title='Settings'>
        <StyledNavLinkDark
          onClick={handleSettingsClick}
          variant='link'
          elementType='link'
          to={Private_Routes.Settings.home.getUrl()}
          aria-label='Go to Settings'
          data-test-id='side-nav-settings-button'>
          <SettingsBIcon type='default' />
        </StyledNavLinkDark>
      </NavTooltip>
      <NavTooltip title='Sign out'>
        <StyledNavAnchorDark
          onClick={() => completeLogoutAndReloadPage()}
          aria-label='Sign out'
          data-test-id='side-nav-logout-button'>
          <LogOutDIcon type='default' />
        </StyledNavAnchorDark>
      </NavTooltip>
    </Stack>
  );
}
function SupportPopover({ onClose, openAiPopover }: { onClose: () => void; openAiPopover: () => unknown }) {
  return (
    <Stack gap={theme.spaces.x8} alignY='center'>
      <MoreNavPopoverStyles />
      <NavTooltip title='Docs'>
        <StyledNavAnchorDark
          onClick={onClose}
          target='_blank'
          aria-label='Go to Docs'
          href={Public_Routes.ProphecyDoc.getUrl()}
          data-test-id='side-nav-docs-button'>
          <BookOpenAIcon type='default' />
        </StyledNavAnchorDark>
      </NavTooltip>
      <NavTooltip title='Product Support'>
        <StyledNavAnchorDark
          onClick={onClose}
          target='_blank'
          aria-label='Go to Product Support'
          href={Public_Routes.Help.getUrl()}
          data-test-id='side-nav-product-support-button'>
          <LifeBuoyBIcon type='default' />
        </StyledNavAnchorDark>
      </NavTooltip>
      {showAiAssistant ? (
        <NavTooltip title='AI Assistant'>
          <StyledNavAnchorDark onClick={openAiPopover} target='_blank'>
            <MessageChatSquareIcon type='default' />
          </StyledNavAnchorDark>
        </NavTooltip>
      ) : null}
    </Stack>
  );
}
type SideNavBarProps = {
  isPlanExpired: boolean;
  isOnboardingRequired: boolean;
};

function useTranspilerEnabledState({
  isPlanExpired,
  isTranspilerNotUsable
}: {
  isPlanExpired: boolean;
  isTranspilerNotUsable: boolean;
}) {
  const globalConfigManager = useGlobalVariables();
  const isTranspilerEnabledByGobalFlag = globalConfigManager.isImportEnabled || isDebugMode();

  const [isTranspilerEnabled, setIsTranspilerEnabled] = useState(
    !(isPlanExpired || isTranspilerNotUsable || !isTranspilerEnabledByGobalFlag)
  );

  const hasTranspilerAccess = useHasTranspilerAccess();

  const { disableTranspiler, disabledMessage } = useEditionConfig();

  const disableTranspilerButton = disableTranspiler || (hasTranspilerAccess ? false : !isTranspilerEnabled);

  const data = useAppMetadata();

  const userId = data.user?.id;
  const variables = { uid: userId || '' };
  const { data: userTeamsAspectData, isSuccess: isUserTeamsAspectQuerySuccess } = useGraphQlQuery(
    [Entity.User, Entity.Team],
    UserTeamsInfoAspectQuery,
    { enabled: Boolean(!isTranspilerNotUsable && !isTranspilerEnabledByGobalFlag && userId) },
    variables
  );

  useEffect(() => {
    if (userTeamsAspectData?.User && isUserTeamsAspectQuerySuccess) {
      const isTranspilerEnabledForUser = userTeamsAspectData?.User.teams.some((team) =>
        team.InfoAspect.some((aspect) => {
          if (aspect.AspectName === AspectKind.Info) {
            const { transpilerEnabled } = parseJSON<{ transpilerEnabled: boolean }>(aspect.AspectValue, {
              transpilerEnabled: false
            });

            return transpilerEnabled;
          }

          return false;
        })
      );

      setIsTranspilerEnabled(isTranspilerEnabledForUser);
    }
  }, [isUserTeamsAspectQuerySuccess, userTeamsAspectData]);

  const { supportedTranspilerType } = useEnabledTranspilersAndLanguages();

  const overridePermissions = supportedTranspilerType?.size && supportedTranspilerType.size > 0;

  return {
    showTranspilerButton: overridePermissions || hasTranspilerAccess !== false,
    isTranspilerEnabled: overridePermissions || !disableTranspilerButton,
    transpilerDisabledMessage: overridePermissions ? '' : disabledMessage
  };
}

export function SideNavBar({ isPlanExpired, isOnboardingRequired }: SideNavBarProps) {
  const globalConfigManager = useGlobalVariables();
  const isSapEnabled = globalConfigManager.isSapEnabled;
  const isFabricSet = globalConfigManager.getFabricStatus;
  const [isMorePopover, toggleMorePopover] = useState(false);
  const [isSupportMorePopover, toggleSupportMorePopover] = useState(false);
  const [aiPopoverOpen, setAiPopoverOpen] = useState(false);

  const { isTranspilerEnabled, showTranspilerButton, transpilerDisabledMessage } = useTranspilerEnabledState({
    isPlanExpired,
    isTranspilerNotUsable: !isFabricSet || isOnboardingRequired
  });

  const navigate = useNavigate();
  const fabrics = useUserFabrics();

  const isSharedProjectOrReplayMode = isShareMode() || isReplayMode(); // to hide logout and settings in share and replay mode
  const { isGuestUserLoggedIn } = useReplayModeType();

  const isProfessionalEditionEnabled = globalConfigManager.isProfessionalEditionEnabled;

  const isLogoutAndSettingHidden = Boolean(
    isProfessionalEditionEnabled || (isSharedProjectOrReplayMode && !isGuestUserLoggedIn)
  );

  const envConfig = useEnvConfig();

  const editionConfig = useEditionConfig();

  const openLastVisitedIDE = (e: React.MouseEvent, lsKey: string) => {
    // Note: we also need to handle onClick for as if pipeline url change, the sidebar doesn't rerender and the url doesn't update
    const ideUrl = localStorage.getItem(lsKey);
    if (ideUrl) {
      e.preventDefault();
      navigate(ideUrl);
    }
  };

  const openAiPopover = function () {
    setAiPopoverOpen(true);
    toggleSupportMorePopover(false);
  };

  const [fabricMessage, setFabricMessage] = useState<string>();

  const navItemProps = (entityName: string) => {
    let message = '';
    if (isPlanExpired) {
      message = `You are trying to access ${entityName}. Your plan has been expired. Please contact support team.`;
    } else if (isOnboardingRequired) {
      message = `You are trying to access ${entityName}. Please complete onboarding steps first.`;
    }

    let onDisabledNavClick: Callable | undefined;
    if (!message && !isFabricSet) {
      onDisabledNavClick = () =>
        setFabricMessage(`You are trying to access ${entityName}. Please setup a Fabric first.`);
    }

    if (!message) {
      message = `${entityName} service is disabled. Please contact support team.`;
    }

    return {
      message,
      redirectURL: isOnboardingRequired ? Private_Routes.Onboarding.getUrl() : '',
      onDisabledNavClick
    };
  };

  const isSparkSupported = useIsSparkSupported();
  const pathname = useLocation().pathname;
  const isOnboardingPage = hasOnboardingUrl(pathname);
  const guestUserClick = useGuestUserClick();
  const { setReplayCompletedUserPreference, skipOnboarding } = useSetReplayUserPreference();
  const handleProphecyLogoClick = async (e: React.MouseEvent) => {
    e.preventDefault(); // Always prevent the default Link navigation first
    // if shared or replay mode, handle guest user click
    if (isOnboardingPage && globalConfigManager.isProfessionalEditionEnabled) {
      const allowedDialogResult = await handleOnboardingSkip(
        isOnboardingPage,
        '',
        computeOkButtonText(globalConfigManager.isProfessionalEditionEnabled, isOnboardingPage, pathname)
      );
      if (allowedDialogResult === DialogResponseAction.okButton) {
        skipOnboarding();
        setTimeout(() => {
          navigate(Private_Routes.Home.getUrl());
        }, 500);
        return;
      } else {
        return false;
      }
    }
    if (isSharedProjectOrReplayMode) {
      await setReplayCompletedUserPreference();
      const response = await guestUserClick();

      switch (response) {
        case GuestUserClickResultEnum.login:
          redirectToAuth();
          break;
        case GuestUserClickResultEnum.leave:
          window.onbeforeunload = null;
          navigateAndRefresh(Private_Routes.Home.getUrl());
          break;
        case GuestUserClickResultEnum.cancel:
          // User cancelled, do nothing
          break;
      }
      return;
    }
    navigate(Private_Routes.Home.getUrl());
  };

  return (
    <>
      <StyledSideNavbar align='space-between'>
        <Stack gap={theme.spaces.x8} alignY='center'>
          <>
            <StyledLogoLink
              to={Private_Routes.Home.getUrl()}
              onClick={handleProphecyLogoClick}
              aria-label='Go to Prophecy dashboard'
              data-test-id='prophecyLogo'>
              <StyledLogo />
            </StyledLogoLink>
            <Divider />
          </>
          <>
            <NavItem
              {...navItemProps('Metadata')}
              to={Private_Routes.User.home.getUrl()}
              disabled={isPlanExpired || !isFabricSet || isOnboardingRequired}
              title='Metadata'
              dataTestId='side-nav-metadata-button'>
              <MetadataIcon size='m' type='extended' />
            </NavItem>
            <NavItem
              {...navItemProps('Entity page')}
              to={Private_Routes.Create_Entity.home.getUrl()}
              disabled={isPlanExpired || isOnboardingRequired}
              title='Create Entity'
              dataTestId='side-nav-create-entity-button'>
              <PlusIcon type='default' size='m' />
            </NavItem>
            <Divider />
            <NavItem
              {...navItemProps(`${Entity.Pipeline} IDE`)}
              onClick={(e) => openLastVisitedIDE(e, LAST_VISITED_IDE_URL)}
              to={Private_Routes.IDE_Home.getUrl({ entity: Entity.Pipeline.toLowerCase() })}
              activeOnRoutes={[
                Private_Routes.IDE_Home.url.replace(':entity', Entity.Pipeline.toLowerCase()),
                Private_Routes.IDE.url.replace(':entity', Entity.Pipeline.toLowerCase()),
                Private_Routes.IDE.url.replace(':entity', Entity.Configuration.toLowerCase()),
                Private_Routes.IDE.url.replace(':entity', Entity.Lineage.toLowerCase()),
                Private_Routes.SQL_IDE.url
              ]}
              disabled={isPlanExpired || !isFabricSet || isOnboardingRequired}
              title='IDE'
              dataTestId='side-nav-ide-button'>
              <EntityIcon hideTooltip={true} entity={Entity.Pipeline} size='m' />
            </NavItem>

            {!isSapEnabled && (
              <>
                {globalConfigManager.isPlaygroundEnabled && (
                  <NavItem
                    {...navItemProps('Gem Builder')}
                    to={Private_Routes.Gems.getUrl()}
                    disabled={!isFabricSet || isOnboardingRequired}
                    title='Gem Builder'
                    dataTestId='side-nav-gem-builder-button'>
                    <GemIcon type='extended' size='m' />
                  </NavItem>
                )}
                {isSparkSupported && (
                  <NavItem
                    {...navItemProps(`${Entity.Job} IDE`)}
                    onClick={(e) => openLastVisitedIDE(e, LAST_JOB_IDE_URL)}
                    to={Private_Routes.IDE_Home.getUrl({ entity: Entity.Job.toLowerCase() })}
                    activeOnRoutes={[
                      Private_Routes.IDE_Home.url.replace(':entity', Entity.Job.toLowerCase()),
                      Private_Routes.IDE.url.replace(':entity', Entity.Job.toLowerCase())
                    ]}
                    disabled={isPlanExpired || !isFabricSet || isOnboardingRequired}
                    title='Job'
                    dataTestId='side-nav-job-button'>
                    <EntityIcon hideTooltip={true} entity={Entity.Job} size='m' />
                  </NavItem>
                )}
              </>
            )}

            {envConfig.observability.enabled && (
              <NavItem
                {...navItemProps('Observability')}
                onClick={(e) => openLastVisitedIDE(e, LAST_OBSERVATION_IDE_URL)}
                to={Private_Routes.IDE_Home.getUrl({ entity: Entity.Observation.toLowerCase() })}
                activeOnRoutes={[
                  Private_Routes.IDE_Home.url.replace(':entity', Entity.Observation.toLowerCase()),
                  Private_Routes.IDE.url.replace(':entity', Entity.Observation.toLowerCase())
                ]}
                disabled={
                  !globalConfigManager.isDeploymentEnabled || isPlanExpired || !isFabricSet || isOnboardingRequired
                }
                title='Observability'
                dataTestId='side-nav-observability-button'>
                <ActivityIcon type='default' size='m' />
              </NavItem>
            )}
            {editionConfig.isLineageEnabled && (
              <NavItem
                {...navItemProps('Lineage')}
                to={Private_Routes.Lineage_Search.getUrl(undefined)}
                disabled={
                  !globalConfigManager.isLineageEnabled || isPlanExpired || !isFabricSet || isOnboardingRequired
                }
                title='Lineage'
                dataTestId='side-nav-lineage-button'>
                <EntityIcon hideTooltip entity={Entity.Lineage} size='m' />
              </NavItem>
            )}
            <NavItem
              {...navItemProps(getAppEntityName(Entity.App))}
              to={Private_Routes.Apps.home.getUrl()}
              disabled={isPlanExpired || !isFabricSet || isOnboardingRequired}
              title={getAppEntityName(Entity.App)}
              dataTestId='side-nav-apps-button'>
              <EntityIcon hideTooltip entity={Entity.App} size='m' />
            </NavItem>
            {showTranspilerButton && (
              <NavItem
                {...navItemProps('Import')}
                to={Private_Routes.Transpiler.getUrl()}
                disabled={!isTranspilerEnabled}
                message={`Import is disabled. ${transpilerDisabledMessage}`}
                title='Import'
                dataTestId='side-nav-import-button'>
                <DownloadDIcon type='default' size='m' />
              </NavItem>
            )}
          </>
        </Stack>
        <Stack gap={theme.spaces.x8} alignY='center'>
          <NavItem
            {...navItemProps('Package Hub')}
            to={HUB_URL.PACKAGE}
            disabled={isPlanExpired || !isFabricSet || isOnboardingRequired}
            title='Package Hub'
            dataTestId='side-nav-package-hub-button'>
            <BuildingBIcon type='default' size='m' />
          </NavItem>
          <Divider />

          {!globalConfigManager.isSapEnabled && (
            <Popover
              placement='right'
              overlayClassName='side-nav-more-popover'
              placementOffset={parseInt(resolveCssVariable(theme.spaces.x24), 10)}
              align='end'
              visible={isSupportMorePopover}
              onVisibleChange={toggleSupportMorePopover}
              alignOffset={-10}
              overlay={
                <SupportPopover openAiPopover={openAiPopover} onClose={() => toggleSupportMorePopover(false)} />
              }>
              <StyledNavButton aria-label='Go to Help and Support' data-test-id='side-nav-help-more-nav'>
                <HelpCircleIcon type='default' />
              </StyledNavButton>
            </Popover>
          )}
          {!isLogoutAndSettingHidden && (
            <Popover
              placement='right'
              overlayClassName='side-nav-more-popover'
              placementOffset={parseInt(resolveCssVariable(theme.spaces.x24), 10)}
              align='end'
              visible={isMorePopover}
              onVisibleChange={toggleMorePopover}
              alignOffset={-10}
              overlay={
                <MoreNavPopover
                  onClose={() => toggleMorePopover(false)}
                  disabled={!isFabricSet || isOnboardingRequired}
                />
              }>
              <StyledNavButton aria-label='Go to more options' data-test-id='side-nav-more-nav'>
                <DotsHorizontalIcon type='default' size='m' />
              </StyledNavButton>
            </Popover>
          )}
          {isWebSocketLoggingEnabled() && (
            <NavTooltip title='Download WebSocket logs'>
              <StyledNavButton
                onClick={async () => {
                  const hasLogs = await downloadWebSocketLogsViaWorker();
                  if (!hasLogs) {
                    toast.info({
                      content: 'No WebSocket logs to download.',
                      'data-test-id': 'websocket-logs-empty-toast'
                    });
                  }
                }}
                aria-label='Download WebSocket logs'
                data-test-id='side-nav-download-websocket-logs-button'>
                <DownloadBIcon type='default' size='m' />
              </StyledNavButton>
            </NavTooltip>
          )}
        </Stack>
      </StyledSideNavbar>
      {showAiAssistant && <AiChatButton isOpen={aiPopoverOpen} onClose={() => setAiPopoverOpen(false)} />}
      {fabricMessage && (
        <FabricSetupDialog
          message={fabricMessage}
          onClose={() => setFabricMessage('')}
          navigate={navigate}
          fabrics={fabrics}
          globalConf={globalConfigManager}
        />
      )}
    </>
  );
}
